import { MenuItem } from "@mui/material";
import React from "react";
import styled from "styled-components";

import { DockedSidebar } from "../../../CommonUI/BuilderUI/DockSidebar";
import { VerticalButton } from "../../../CommonUI/CommonStyles/VerticalButton";
import { CopyWhiteIcon } from "../../../CommonUI/Icon/CopyWhiteIcon";
import { CutWhiteIcon } from "../../../CommonUI/Icon/CutWhiteIcon";
import { DeleteWhiteIcon } from "../../../CommonUI/Icon/DeleteWhiteIcon";
import { ReactRenderHook } from "../../../CommonUI/ReactRenderHook";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { FlowConstants } from "../../../rule-engine-core/Flow/Utilities/FlowConstants";
import { CandidateSequenceStack } from "../CandidateSequenceStack";
import { FlowEditContext } from "../../FlowEditContext";
import { FlowStepSequence } from "../../../rule-engine-core/Flow/Structure/FlowStepSequence";
import { IFlowStepSequence } from "../../../rule-engine-core/Flow/Structure/IFlowStepSequence";
import { FlowConfiguration } from "../../../rule-engine-core/Flow/FlowConfiguration";
import { EditLayer } from "./XFlowEditor.EditLayer";
import { ErrorMessageForSequence } from "./XFlowEditor.ErrorMessageForSequence";
import { SeqeuenceStrategyEditDialog } from "./XFlowEditor.SeqeuenceStrategyEditDialog";
import { StepStrategyEditDialog } from "./XFlowEditor.StepStrategy";
import { Notification } from "./XFlowEditor.Notification";
import { DropdownIcon } from "../../../CommonUI/Icon/DropdownIcon";
import { FlowEditorExtraConnections } from "./XFlowEditor.ExtraConnection";
import { ConnectionLine } from "./ConnectionLine";
import { ValidationInformation } from "../../../Rule/DesignUI/ValidationInformation";
import { FlowSequenceStack } from "../FlowSequenceStack";
import { ContextMenuForControl } from "../../../CommonUI/CommonStyles/ContextMenuForControl";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

const DesignPanelWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    overflow: auto;
`;

const DesignPanel = styled.div`
    height: 100%;
    background: ${UIStyleConstants.WHITE_SEMI_DARK_COLOR};
    position: relative;
    overflow: auto;
`;

const InformationPanelDiv = styled.div<{ expandedWidth: number }>`
    position: fixed;
    right: ${(props) => props.expandedWidth + 24}px;
    bottom: 24px;

    display: flex;
    align-items: flex-end;

    > * {
        margin-left: 12px;

        &:first-child {
            margin-left: 0;
        }
    }
`;

export const FlowDesignPage = function (props: { flow: FlowConfiguration; editContext: FlowEditContext }) {
    function renderContextMenuUI() {
        let contextControl = props.editContext.contextControl;
        if (contextControl) {
            return (
                <ContextMenuForControl
                    open={!!contextControl}
                    onClose={() => (props.editContext.contextControl = null)}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextControl.mouseY !== null && contextControl.mouseX !== null
                            ? { top: contextControl.mouseY, left: contextControl.mouseX }
                            : undefined
                    }
                >
                    <MenuItem
                        onClick={() => props.editContext.onCopy(contextControl!.selectedId)}
                        data-testid="btn-context-copy"
                    >
                        <CopyWhiteIcon /> Copy
                    </MenuItem>
                    <MenuItem
                        onClick={() => props.editContext.onCut(contextControl!.selectedId)}
                        data-testid="btn-context-cut"
                    >
                        <CutWhiteIcon /> Cut
                    </MenuItem>
                    <MenuItem
                        onClick={() => props.editContext.onDelete(contextControl!.selectedId)}
                        data-testid="btn-context-paste"
                    >
                        <DeleteWhiteIcon /> Delete
                    </MenuItem>
                </ContextMenuForControl>
            );
        }
    }

    const onClickDesigner = (event: React.MouseEvent<HTMLDivElement>) => {
        // if there is any selected control or open panel is opened
        if (event.target === event.currentTarget && (!!props.editContext.selection || props.editContext.isShowEditor)) {
            props.editContext.clearSelection();
        }
    };

    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
        props.editContext.designerScrollTop = event.currentTarget.scrollTop;
    };

    return (
        <ObserveState
            listenTo={props.editContext}
            properties={[FlowEditContext.IS_SHOW_EDITOR, FlowEditContext.DESIGNER_SCROLL_TOP]}
            control={() => (
                <DockedSidebar
                    sidebarWidth={350}
                    sidebar={() => (
                        <EditLayer
                            flow={props.flow}
                            editContext={props.editContext}
                        />
                    )}
                    content={() => {
                        return (
                            <DesignPanelWrapper>
                                <DesignPanel
                                    id={FlowConstants.DESIGN_SURFACE_ID}
                                    onClick={onClickDesigner}
                                    onScroll={onScroll}
                                >
                                    <ReactRenderHook />

                                    <ObserveState
                                        listenTo={props.editContext}
                                        properties={[FlowEditContext.CONNECTIONS_CHANGED, FlowStepSequence.DRAG_OVER]}
                                        control={() => {
                                            return (
                                                <>
                                                    {props.editContext.getLines().map((line) => (
                                                        <ConnectionLine
                                                            key={`${line.value.fromId}-${line.value.toId}`}
                                                            line={line}
                                                            editContext={props.editContext}
                                                            flow={props.flow}
                                                        />
                                                    ))}
                                                </>
                                            );
                                        }}
                                    />

                                    <ObserveState
                                        listenTo={props.editContext.flow}
                                        properties={[FlowConfiguration.CANDIDATE_SEQUENCES]}
                                        control={() => (
                                            <>
                                                {props.editContext
                                                    .getCandidateSequences()
                                                    .filter((s: IFlowStepSequence) => {
                                                        // do not render on positioned
                                                        return s.x !== -1 && s.y !== -1;
                                                    })
                                                    .map((c: IFlowStepSequence) => (
                                                        <CandidateSequenceStack
                                                            key={c._id}
                                                            candidate={c}
                                                            editContext={props.editContext}
                                                        />
                                                    ))}
                                            </>
                                        )}
                                    />

                                    {props.flow.sequences.map((sequence: FlowStepSequence) => {
                                        return (
                                            <ObserveState
                                                listenTo={sequence}
                                                key={sequence._id}
                                                properties={[
                                                    FlowStepSequence.STRATEGY,
                                                    FlowStepSequence.STEPS_CHANGED,
                                                    FlowStepSequence.COLOR,
                                                    FlowStepSequence.EXPANDING,
                                                    FlowStepSequence.DRAG_OVER,
                                                ]}
                                                control={() => (
                                                    <ObserveState
                                                        listenTo={sequence}
                                                        control={() => {
                                                            return (
                                                                <ObserveState
                                                                    listenTo={props.editContext}
                                                                    properties={[FlowStepSequence.DRAG_OVER]}
                                                                    control={() => (
                                                                        <FlowSequenceStack
                                                                            sequence={sequence}
                                                                            editContext={props.editContext!}
                                                                            flow={sequence.flow!}
                                                                        />
                                                                    )}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                )}
                                            />
                                        );
                                    })}

                                    <ObserveState
                                        listenTo={props.editContext}
                                        properties={[FlowEditContext.CONTEXT_CONTROl]}
                                        control={() => <>{renderContextMenuUI()}</>}
                                    />

                                    <Notification editContext={props.editContext} />
                                    <SeqeuenceStrategyEditDialog editContext={props.editContext} />
                                    <StepStrategyEditDialog editContext={props.editContext} />
                                    <ErrorMessageForSequence editContext={props.editContext} />
                                    <ObserveState
                                        listenTo={props.editContext}
                                        properties={[FlowEditContext.SHOW_ERROR_BRANCH, FlowStepSequence.DRAG_OVER]}
                                        control={() => (
                                            <InformationPanelDiv
                                                expandedWidth={props.editContext.isShowEditor ? 350 : 0}
                                                id="information_div"
                                            >
                                                <ObserveState
                                                    listenTo={props.flow.errorSequence}
                                                    control={() => (
                                                        <FlowSequenceStack
                                                            sequence={props.flow.errorSequence}
                                                            editContext={props.editContext}
                                                            flow={props.flow}
                                                        />
                                                    )}
                                                />
                                                <ObserveState
                                                    listenTo={props.flow.endSequence}
                                                    control={() => (
                                                        <FlowSequenceStack
                                                            sequence={props.flow.endSequence}
                                                            editContext={props.editContext}
                                                            flow={props.flow}
                                                        />
                                                    )}
                                                />
                                            </InformationPanelDiv>
                                        )}
                                    />
                                </DesignPanel>
                                <FlowEditorExtraConnections
                                    editContext={props.editContext}
                                    flow={props.flow}
                                />

                                <ObserveState
                                    listenTo={props.editContext}
                                    control={() => <ValidationInformation provider={props.flow} />}
                                />
                            </DesignPanelWrapper>
                        );
                    }}
                    open={props.editContext.isShowEditor}
                    onClose={() => props.editContext.hideEditLayer}
                    toogleIcon={() => (
                        <VerticalButton
                            variant="contained"
                            onClick={() => props.editContext.toggleEditLayer()}
                            endIcon={<DropdownIcon />}
                            open={props.editContext.isShowEditor}
                        >
                            Edit: {props.flow.find(props.editContext.selection || "")?.name || "Flow"}
                        </VerticalButton>
                    )}
                />
            )}
        />
    );
};
